// @wc-ignore-file
// (Search values and route segments are data, not copy — a translated
// "support" would silently stop matching under a non-English locale.)

import { isSafeSegment, parseWorkspacePath } from "@/features/workspaces/workspace-path";

/**
 * CR-116 — the `?incognito=support` route flag. It only selects the load path
 * in `$slug.tsx`; it is never proof of access (the RPCs behind it raise 42501
 * unless `is_platform_admin()`).
 */
export const INCOGNITO_SEARCH_VALUE = "support" as const;

export type IncognitoSearch = {
  incognito?: typeof INCOGNITO_SEARCH_VALUE;
};

/**
 * Child routes of `/workspaces/$slug` reachable in incognito mode, as the path
 * after the slug (`""` = the workspace home). Everything else fails closed.
 * Project pages (`projects/<slug>/…`) have their own list below (CR-165).
 * Each entry's page must branch on `useWorkspaceAccess()` and read only through
 * `incognitoQueries`; member hooks would 42501 or, worse, render empty.
 */
export const INCOGNITO_ALLOWED_SUBPATHS: readonly string[] = Object.freeze([
  "",
  "members",
  "projects",
]);

/**
 * CR-165 D-B — pages of a project (`projects/<projectSlug>/…`) reachable in
 * incognito mode, as the path after the project slug. `*` stands for exactly
 * one non-empty id segment. An entry is added only in the stage that ships its
 * display-only page; everything else (create, ai-team, skills, tools,
 * integrations, agent-keys, unknown paths) fails closed to the project root.
 */
export const INCOGNITO_ALLOWED_PROJECT_SUBPATHS: readonly string[] = Object.freeze([
  "",
  "change-requests",
  "chats",
  "chats/*",
  "cr/*",
  "docs",
  "docs/*",
  "members",
  "messages",
  "settings",
  "tasks",
]);

/** Only the exact literal `support` turns the flag on; anything else is dropped. */
export function parseIncognitoSearch(search: Record<string, unknown>): IncognitoSearch {
  return search.incognito === INCOGNITO_SEARCH_VALUE ? { incognito: INCOGNITO_SEARCH_VALUE } : {};
}

/**
 * Navigation target for an incognito workspace page. Every in-app link inside
 * an incognito view must carry the flag: without it the member load path runs
 * and bounces to `/workspaces`.
 */
export function incognitoWorkspaceLink(slug: string) {
  return {
    to: "/workspaces/$slug",
    params: { slug },
    search: { incognito: INCOGNITO_SEARCH_VALUE },
  } as const;
}

/** Same, for the read-only members list. */
export function incognitoWorkspaceMembersLink(slug: string) {
  return {
    to: "/workspaces/$slug/members",
    params: { slug },
    search: { incognito: INCOGNITO_SEARCH_VALUE },
  } as const;
}

/** Same, for the read-only projects list. */
export function incognitoWorkspaceProjectsLink(slug: string) {
  return {
    to: "/workspaces/$slug/projects",
    params: { slug },
    search: { incognito: INCOGNITO_SEARCH_VALUE },
  } as const;
}

/** Same, for a project's root (it redirects to the first allowed module). */
export function incognitoProjectLink(slug: string, projectSlug: string) {
  return {
    to: "/workspaces/$slug/projects/$projectSlug",
    params: { slug, projectSlug },
    search: { incognito: INCOGNITO_SEARCH_VALUE },
  } as const;
}

/** Same, for the read-only document page (CR-165 stage 4). */
export function incognitoDocumentLink(slug: string, projectSlug: string, docId: string) {
  return {
    to: "/workspaces/$slug/projects/$projectSlug/docs/$docId",
    params: { slug, projectSlug, docId },
    search: { incognito: INCOGNITO_SEARCH_VALUE },
  } as const;
}

/**
 * Same, for the read-only chat page (CR-165 stage 5). AI chats and team
 * message rooms share the `chats/$chatId` route.
 */
export function incognitoChatLink(slug: string, projectSlug: string, chatId: string) {
  return {
    to: "/workspaces/$slug/projects/$projectSlug/chats/$chatId",
    params: { slug, projectSlug, chatId },
    search: { incognito: INCOGNITO_SEARCH_VALUE },
  } as const;
}

/** Where a content row (sidebar item, list row) opens. */
export type IncognitoItemLink =
  | ReturnType<typeof incognitoDocumentLink>
  | ReturnType<typeof incognitoChatLink>;

/** Same, for the read-only change-request page (CR-165 stage 4). */
export function incognitoChangeRequestLink(slug: string, projectSlug: string, crId: string) {
  return {
    to: "/workspaces/$slug/projects/$projectSlug/cr/$crId",
    params: { slug, projectSlug, crId },
    search: { incognito: INCOGNITO_SEARCH_VALUE },
  } as const;
}

/**
 * The project pages the incognito sidebar declares. NOT an allowlist: every
 * caller must still filter through `isIncognitoProjectSubpathAllowed` (the
 * sidebar does, and its test pins the rendered links to the allowlisted ones).
 */
export const INCOGNITO_PROJECT_SECTION_ROUTES = {
  "change-requests": "/workspaces/$slug/projects/$projectSlug/change-requests",
  chats: "/workspaces/$slug/projects/$projectSlug/chats",
  messages: "/workspaces/$slug/projects/$projectSlug/messages",
  docs: "/workspaces/$slug/projects/$projectSlug/docs",
  tasks: "/workspaces/$slug/projects/$projectSlug/tasks",
  members: "/workspaces/$slug/projects/$projectSlug/members",
  settings: "/workspaces/$slug/projects/$projectSlug/settings",
} as const;

export type IncognitoProjectSection = keyof typeof INCOGNITO_PROJECT_SECTION_ROUTES;

/** Same, for one project page. */
export function incognitoProjectSectionLink(
  slug: string,
  projectSlug: string,
  section: IncognitoProjectSection,
) {
  return {
    to: INCOGNITO_PROJECT_SECTION_ROUTES[section],
    params: { slug, projectSlug },
    search: { incognito: INCOGNITO_SEARCH_VALUE },
  } as const;
}

/**
 * True when the segments after `projects/<projectSlug>/` match one allowlist
 * entry, `*` standing for exactly one segment. Callers have already rejected
 * empty, dot and slash-bearing segments.
 */
function matchesProjectSubpath(segments: readonly string[], allowlist: readonly string[]): boolean {
  return allowlist.some((entry) => {
    const pattern = entry === "" ? [] : entry.split("/");
    return (
      pattern.length === segments.length &&
      pattern.every((part, index) => part === "*" || part === segments[index])
    );
  });
}

/**
 * True when a project page (the path after the project slug, e.g. `docs` or
 * `docs/<id>`, `""` for the root) is allowlisted. The incognito sidebar and the
 * project-home resolver use it so they never offer a page the guard bounces.
 */
export function isIncognitoProjectSubpathAllowed(
  subpath: string,
  allowlist: readonly string[] = INCOGNITO_ALLOWED_PROJECT_SUBPATHS,
): boolean {
  const segments = subpath === "" ? [] : subpath.split("/");
  return segments.every(isSafeSegment) && matchesProjectSubpath(segments, allowlist);
}

/**
 * True only when `pathname` is `/workspaces/<slug>` followed by one of the
 * allowlisted workspace sub-paths, or by `projects/<projectSlug>/` and one of
 * the allowlisted project pages (one optional trailing slash tolerated).
 */
export function isIncognitoPathAllowed(
  pathname: string,
  slug: string,
  allowlist: readonly string[] = INCOGNITO_ALLOWED_SUBPATHS,
  projectAllowlist: readonly string[] = INCOGNITO_ALLOWED_PROJECT_SUBPATHS,
): boolean {
  const path = parseWorkspacePath(pathname, slug);
  if (!path) {
    return false;
  }
  if (path.kind === "project") {
    return matchesProjectSubpath(path.segments, projectAllowlist);
  }
  return allowlist.includes(path.subpath);
}

/**
 * Where a refused incognito path lands: the incognito project root for a page
 * of a well-formed project path, else the incognito workspace home.
 */
export function incognitoFallbackLink(pathname: string, slug: string) {
  const path = parseWorkspacePath(pathname, slug);
  if (path?.kind === "project" && isIncognitoProjectSubpathAllowed("")) {
    return incognitoProjectLink(slug, path.projectSlug);
  }
  return incognitoWorkspaceLink(slug);
}
